/**
 * Lightweight analytics integration — supports PostHog OR Plausible.
 *
 * Activate by setting EITHER env var in /app/frontend/.env:
 *   REACT_APP_POSTHOG_KEY=phc_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
 *   REACT_APP_POSTHOG_HOST=https://app.posthog.com   # optional
 *
 *   — or —
 *
 *   REACT_APP_PLAUSIBLE_DOMAIN=industry-nashville.com
 *
 * Both opt-in via env vars. With neither set, every function below
 * is a no-op so the app still works locally + in preview without
 * polluting your real analytics with test events.
 *
 * Privacy: PostHog snippet is loaded with `respect_dnt: true` and
 * Plausible never sets cookies. Both are GDPR/CCPA-safe.
 */

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';
const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;

let _initialized = false;
let _provider = null;
let _posthog = null;

/** Init must be called once on app boot. Idempotent. */
export function initAnalytics() {
  if (_initialized || typeof window === 'undefined') return;
  _initialized = true;

  // ---- PostHog (richer: events + funnels + session replay) ----
  if (POSTHOG_KEY) {
    // Lazy-import so it doesn't bloat the bundle when not in use
    import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          capture_pageview: true,
          respect_dnt: true,
          // Privacy-friendly defaults
          disable_session_recording: true,
          autocapture: false, // only fire events we explicitly track
        });
        _posthog = posthog;
        _provider = 'posthog';
      })
      .catch(() => {
        /* posthog-js not installed yet — silent no-op */
      });
    return;
  }

  // ---- Plausible (lightweight, cookie-free) ----
  if (PLAUSIBLE_DOMAIN) {
    const s = document.createElement('script');
    s.defer = true;
    s.setAttribute('data-domain', PLAUSIBLE_DOMAIN);
    s.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(s);
    _provider = 'plausible';
  }
}

/** Identify the logged-in user (call once after login/register). */
export function identifyUser(userId, traits = {}) {
  if (_provider === 'posthog' && _posthog) {
    _posthog.identify(userId, traits);
  }
  // Plausible doesn't support identity — by design (privacy)
}

/** Fire a named event with optional properties. Safe no-op when no provider. */
export function track(eventName, props = {}) {
  if (_provider === 'posthog' && _posthog) {
    _posthog.capture(eventName, props);
    return;
  }
  if (_provider === 'plausible' && typeof window.plausible === 'function') {
    window.plausible(eventName, { props });
    return;
  }
  // Otherwise: no-op
}

/** Fire on logout. */
export function resetAnalytics() {
  if (_provider === 'posthog' && _posthog) _posthog.reset();
}

/** Returns whether analytics is actually wired up (useful for debug UI). */
export function analyticsProvider() {
  return _provider;
}
